import React, { useState } from 'react';
import { Box, Menu, Typography, useTheme } from '@mui/material';
import { SortContentKey, SortContentOrder } from 'entities/types';
import ArrowUp from 'shared/icons/arrows/arrowUp';
import ArrowDown from 'shared/icons/arrows/arrowDown';
import { makeTransition } from 'shared/lib/utils/utils';
import CommonMenuItem from 'shared/lib/components/menu/commonMenuItem';
import MenuHeader from 'shared/lib/components/menu/menuHeader';

export default function HeaderCell({
  text,
  sort,
  ascText = 'По возрастанию',
  descText = 'По убыванию',
  isArrowReversed,
}: {
  text: string;
  ascText?: string;
  descText?: string;
  isArrowReversed?: boolean;
  sort?: {
    currentValue: { key: SortContentKey; order: SortContentOrder };
    key: SortContentKey;
    onAscClick: () => void;
    onDescClick: () => void;
  };
}) {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [_isHover, setIsHover] = useState(false);

  const open = Boolean(anchorEl);

  const isHover = sort && _isHover;

  const onClick = sort
    ? (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (anchorEl) {
          setAnchorEl(null);
          setIsHover(false);
          return;
        }
        setAnchorEl(e.currentTarget);
      }
    : undefined;

  const renderArrow = () =>
    sort && sort.currentValue.key === sort.key ? (
      sort.currentValue.order === SortContentOrder.ASC ? (
        <ArrowUp sizeKey="16x16" />
      ) : (
        <ArrowDown sizeKey="16x16" />
      )
    ) : isHover ? (
      <ArrowDown sizeKey="16x16" />
    ) : null;

  // В случае сортировки по названию, стрелки должны быть направлены в обратную сторону в соответствии с макетом
  const renderArrowReversed = () =>
    sort && sort.currentValue.key === sort.key ? (
      sort.currentValue.order === SortContentOrder.ASC ? (
        <ArrowDown sizeKey="16x16" />
      ) : (
        <ArrowUp sizeKey="16x16" />
      )
    ) : isHover ? (
      <ArrowDown sizeKey="16x16" />
    ) : null;

  return (
    <Box
      sx={{
        'display': 'flex',
        'gap': 2,
        'alignItems': 'center',
        'cursor': sort ? 'pointer' : 'default',
        '& .icon path': {
          transition: makeTransition(['fill']),
          fill: isHover
            ? theme.palette.primary.main
            : theme.palette.text.primary,
        },
      }}
      onMouseEnter={() => {
        setIsHover(true);
      }}
      onMouseLeave={() => {
        setIsHover(false);
      }}
      onClick={onClick}
    >
      <Typography
        sx={{ whiteSpace: 'nowrap', transition: makeTransition(['color']) }}
        color={isHover ? 'primary.main' : 'text.primary'}
        variant="f16"
      >
        {text}
      </Typography>

      {sort ? (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => {
            setAnchorEl(null);
          }}
        >
          <MenuHeader text="Тип сортировки"></MenuHeader>
          <CommonMenuItem
            onClick={() => {
              sort.onDescClick();
              setAnchorEl(null);
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <ArrowDown sizeKey="16x16"></ArrowDown>
              <Typography variant="f16">{descText}</Typography>
            </Box>
          </CommonMenuItem>
          <CommonMenuItem
            onClick={() => {
              sort.onAscClick();
              setAnchorEl(null);
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <ArrowUp sizeKey="16x16"></ArrowUp>
              <Typography variant="f16">{ascText}</Typography>
            </Box>
          </CommonMenuItem>
        </Menu>
      ) : null}

      {isArrowReversed ? renderArrowReversed() : renderArrow()}
    </Box>
  );
}
